/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */

/**
 * Rooms — the shared dispatch chat (DISPATCH_CHAT_DESIGN.md Phase A).
 * LGH room only. The internal Dispatch room was retired 2026-08-13 (Paula:
 * dispatchers never used it to talk to each other, and Rosie auto-booking
 * from loose chat caused bad trips — TRP-2026-2938, 2949). The backend
 * refuses posts to the old room; its history is preserved in the DB.
 * In the LGH room, dispatcher messages run the structured booking flow
 * on behalf of LGH (the bridge workflow / phone-in bookings).
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../utils/api';

const user = JSON.parse(localStorage.getItem('nemt_user') || '{}');
const myName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'dispatcher';

const ROOM_TABS = [
  { key: 'lgh', label: 'LGH', hint: 'Bookings you type here are entered on behalf of LGH (structured — creates real trips).' },
];

function MessageBubble({ m }) {
  if (m.message_type === 'booking_card') {
    return (
      <div style={{ alignSelf: 'center', maxWidth: '92%', background: '#E1F5EE', border: '1px solid #5DCAA5', borderRadius: 10, padding: '10px 14px', margin: '4px 0', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#0F6E56', marginBottom: 4 }}>BOOKING</div>
        <div style={{ fontSize: 13, color: '#085041', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{m.body}</div>
        {m.ref_transport_number && (
          <div style={{ fontSize: 11, color: '#0F6E56', marginTop: 4 }}>{m.ref_transport_number}</div>
        )}
      </div>
    );
  }
  if (m.message_type === 'system_event' || m.author_type === 'system') {
    return (
      <div style={{ alignSelf: 'center', maxWidth: '90%', background: '#FAEEDA', color: '#854F0B', borderRadius: 8, padding: '6px 12px', fontSize: 12, textAlign: 'center', margin: '2px 0' }}>
        {m.body}
      </div>
    );
  }
  const isRosie = m.author_type === 'rosie';
  const mine = !isRosie && m.author_name === myName;
  return (
    <div style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '80%', margin: '2px 0' }}>
      <div style={{ fontSize: 10, color: '#999', margin: mine ? '0 6px 2px 0' : '0 0 2px 6px', textAlign: mine ? 'right' : 'left' }}>
        {isRosie ? 'Rosie' : (m.author_name || 'dispatcher')} · {new Date(m.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })}
      </div>
      <div style={{
        background: mine ? '#185FA5' : (isRosie ? 'white' : '#EEEDFE'),
        color: mine ? 'white' : '#333',
        border: isRosie ? '0.5px solid #e0e0db' : 'none',
        borderRadius: 14, padding: '9px 13px', fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {m.body}
      </div>
    </div>
  );
}

export default function Rooms() {
  // Single room since the Dispatch tab retirement; ?room= deep links all land here
  const [activeRoom, setActiveRoom] = useState('lgh');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const lastIdRef = useRef(null);
  const activeRoomRef = useRef(activeRoom);
  activeRoomRef.current = activeRoom;
  // Held capacity drafts (PB-2): Rosie's capacity answers to LGH wait here
  // for a dispatcher to release. `review` holds a fresher answer surfaced
  // when a stale draft was re-checked on release.
  const [held, setHeld] = useState([]);
  const [review, setReview] = useState({});
  const [busyHeld, setBusyHeld] = useState({});

  const scrollToBottom = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  };

  const loadFull = useCallback(async (roomKey) => {
    try {
      const res = await api.get(`/chat/rooms/${roomKey}/messages`);
      if (activeRoomRef.current !== roomKey) return;
      setMessages(res.data.messages || []);
      lastIdRef.current = res.data.messages?.length ? res.data.messages[res.data.messages.length - 1].id : null;
      setError(null);
      setTimeout(scrollToBottom, 50);
    } catch (e) {
      setError('Could not load messages');
    }
  }, []);

  // Full load on room switch
  useEffect(() => {
    setMessages([]);
    lastIdRef.current = null;
    loadFull(activeRoom);
  }, [activeRoom, loadFull]);

  // Incremental poll every 5 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      const roomKey = activeRoomRef.current;
      try {
        if (!lastIdRef.current) return;
        const res = await api.get(`/chat/rooms/${roomKey}/messages`, { params: { afterId: lastIdRef.current } });
        if (activeRoomRef.current !== roomKey) return;
        const fresh = res.data.messages || [];
        if (fresh.length > 0) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            return [...prev, ...fresh.filter((m) => !seen.has(m.id))];
          });
          lastIdRef.current = fresh[fresh.length - 1].id;
          setTimeout(scrollToBottom, 50);
        }
      } catch (e) {}
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput('');
    const optimistic = {
      id: `tmp-${Date.now()}`, author_type: 'dispatcher', author_name: myName,
      body: text, message_type: 'text', created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setTimeout(scrollToBottom, 50);
    try {
      const res = await api.post(`/chat/rooms/${activeRoom}/messages`, {
        body: text,
        authorName: myName,
        authorEmail: user.email || '',
        authorUserId: user.id || null,
      });
      // Replace optimistic message + append Rosie's reply; poller fills any gaps
      setMessages((prev) => {
        const withoutTmp = prev.filter((m) => m.id !== optimistic.id);
        const additions = [res.data.message];
        if (res.data.rosie) additions.push(res.data.rosie);
        const seen = new Set(withoutTmp.map((m) => m.id));
        return [...withoutTmp, ...additions.filter((m) => m && !seen.has(m.id))];
      });
      if (res.data.rosie) lastIdRef.current = res.data.rosie.id;
      else if (res.data.message) lastIdRef.current = res.data.message.id;
      if (res.data.bookingCardPosted) loadFull(activeRoom);
      setTimeout(scrollToBottom, 80);
    } catch (e) {
      setError('Message failed to send');
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  // Held-capacity review (dispatchers only; poll regardless of active tab)
  const loadHeld = useCallback(async () => {
    try {
      const res = await api.get('/chat/held');
      setHeld(res.data.held || []);
    } catch (e) { /* held panel is best-effort */ }
  }, []);
  useEffect(() => {
    loadHeld();
    const id = setInterval(loadHeld, 8000);
    return () => clearInterval(id);
  }, [loadHeld]);

  const setHeldBusy = (id, v) => setBusyHeld((b) => ({ ...b, [id]: v }));

  const releaseHeld = async (id, useFresh = false) => {
    setHeldBusy(id, true);
    try {
      const res = await api.post(`/chat/held/${id}/release`, useFresh ? { useFresh: true } : {});
      if (res.data.changed) {
        setReview((r) => ({ ...r, [id]: { fresh: res.data.freshAnswer, prev: res.data.previousAnswer } }));
      } else {
        setReview((r) => { const n = { ...r }; delete n[id]; return n; });
        setHeld((h) => h.filter((d) => d.id !== id));
        if (activeRoomRef.current === 'lgh') loadFull('lgh');
      }
    } catch (e) { /* leave it in the list to retry */ } finally { setHeldBusy(id, false); }
  };

  const discardHeld = async (id) => {
    setHeldBusy(id, true);
    try {
      await api.post(`/chat/held/${id}/discard`);
      setReview((r) => { const n = { ...r }; delete n[id]; return n; });
      setHeld((h) => h.filter((d) => d.id !== id));
    } catch (e) { /* retry on next action */ } finally { setHeldBusy(id, false); }
  };

  const tab = ROOM_TABS.find((t) => t.key === activeRoom);

  return (
    <div className="rooms-shell" style={{ display: 'flex', flexDirection: 'column', maxWidth: 760, width: '100%', margin: '0 auto', overflowX: 'hidden' }}>
      {/* Height accounts for the mobile bottom nav so the composer never hides
          under it (DISPATCH-SPEED LAW: no dead-ends). dvh handles mobile
          browser chrome better than vh. */}
      <style>{`
        .rooms-shell { height: calc(100vh - 60px); }
        @media (max-width: 768px) {
          .rooms-shell { height: calc(100dvh - 60px - 64px); }
        }
      `}</style>
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px 0' }}>
        {ROOM_TABS.map((t) => (
          <button key={t.key} onClick={() => setActiveRoom(t.key)}
            style={{
              padding: '8px 18px', fontSize: 14, fontWeight: 500, borderRadius: '10px 10px 0 0', cursor: 'pointer',
              border: '0.5px solid #e0e0db', borderBottom: 'none',
              background: activeRoom === t.key ? 'white' : '#f0f0ec',
              color: activeRoom === t.key ? '#185FA5' : '#888',
            }}>
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ background: '#E6F1FB', border: '0.5px solid #B8D4EE', padding: '6px 14px', fontSize: 11, color: '#185FA5' }}>
        {tab.hint}
      </div>
      {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '6px 14px', fontSize: 12 }}>{error}</div>}
      {held.length > 0 && (
        <div style={{ background: '#FFF7E6', border: '1px solid #F0C36D', borderRadius: 8, margin: '6px 0', padding: '8px 12px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#8A5A00', marginBottom: 6 }}>
            {held.length} capacity {held.length === 1 ? 'answer' : 'answers'} waiting for release to LGH
          </div>
          {held.map((d) => {
            const r = review[d.id];
            const stale = d.ageMinutes >= 15;
            return (
              <div key={d.id} style={{ background: 'white', border: '1px solid #eee', borderRadius: 6, padding: '8px 10px', marginBottom: 6 }}>
                <div style={{ fontSize: 12, color: '#666' }}>LGH asked: <span style={{ color: '#333' }}>{d.question}</span></div>
                <div style={{ fontSize: 13, color: '#111', margin: '4px 0', whiteSpace: 'pre-wrap' }}>Rosie: {r ? r.fresh : d.answer}</div>
                <div style={{ fontSize: 11, color: stale ? '#B25E00' : '#999' }}>
                  held {d.ageMinutes} min{stale ? ' · will re-check before posting' : ''}{d.refreshed ? ' · re-checked' : ''}
                </div>
                {r ? (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 11, color: '#B25E00', marginBottom: 4 }}>Availability changed since it was drafted — this is the current answer. Post it?</div>
                    <button disabled={busyHeld[d.id]} onClick={() => releaseHeld(d.id, true)}
                      style={{ background: '#0F6E56', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer', marginRight: 6 }}>Post updated answer</button>
                    <button disabled={busyHeld[d.id]} onClick={() => discardHeld(d.id)}
                      style={{ background: 'white', color: '#A32D2D', border: '1px solid #E3B3B3', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>Discard</button>
                  </div>
                ) : (
                  <div style={{ marginTop: 6 }}>
                    <button disabled={busyHeld[d.id]} onClick={() => releaseHeld(d.id)}
                      style={{ background: '#0F6E56', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer', marginRight: 6 }}>Release to LGH</button>
                    <button disabled={busyHeld[d.id]} onClick={() => discardHeld(d.id)}
                      style={{ background: 'white', color: '#A32D2D', border: '1px solid #E3B3B3', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>Discard (I'll answer)</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', background: '#f7f7f5', border: '0.5px solid #e0e0db', padding: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {messages.length === 0 && (
          <div style={{ margin: 'auto', color: '#999', fontSize: 13, textAlign: 'center' }}>
            No messages yet. Type an LGH booking here — e.g. "WC 1430 to Ephrata Manor, rm 214, J. Smith" — and Rosie will confirm and create the trip.
          </div>
        )}
        {messages.map((m) => <MessageBubble key={m.id} m={m} />)}
        {sending && <div style={{ alignSelf: 'flex-start', fontSize: 12, color: '#999', fontStyle: 'italic', padding: '4px 8px' }}>Rosie is thinking...</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 12, background: 'white', border: '0.5px solid #e0e0db', borderTop: 'none' }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a booking on behalf of LGH..."
          style={{ flex: 1, fontSize: 15, padding: '10px 14px', border: '1px solid #ddd', borderRadius: 20, outline: 'none' }}
        />
        <button onClick={send} disabled={sending || !input.trim()}
          style={{ background: '#185FA5', color: 'white', border: 'none', borderRadius: 20, padding: '10px 22px', fontSize: 14, fontWeight: 500, cursor: 'pointer', opacity: sending || !input.trim() ? 0.5 : 1 }}>
          Send
        </button>
      </div>
    </div>
  );
}
